"use client";

import { useState } from "react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { showToast } from "@/app/components/ui/Toast";
import Modal from "@/app/components/ui/Modal";
import {
  FacebookShareButton,
  LineShareButton,
  TwitterShareButton,
  FacebookMessengerShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  LineIcon,
  FacebookMessengerIcon,
  WhatsappIcon,
  EmailIcon,
  XIcon,
} from "react-share";
import { CheckIcon, CopyIcon } from "lucide-react";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  title: string;
  isEmbeddedInModal?: boolean;
  onExited?: () => void;
}

export default function ShareModal({
  isOpen,
  onClose,
  shareUrl,
  title,
  isEmbeddedInModal = false,
  onExited,
}: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showToast("คัดลอกแล้ว");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const shareBody = (
    <>
      <div className="mb-8 grid grid-cols-3 gap-6">
        <div className="group flex flex-col items-center gap-2">
          <LineShareButton url={shareUrl} title={title}>
            <div className="h-14 w-14 overflow-hidden rounded-2xl transition-transform duration-200 group-hover:scale-105">
              <LineIcon size={56} borderRadius={16} />
            </div>
          </LineShareButton>
          <span className="text-xs font-semibold text-aera-slate">LINE</span>
        </div>

        <div className="group flex flex-col items-center gap-2">
          <FacebookShareButton url={shareUrl} hashtag="#Aera">
            <div className="h-14 w-14 overflow-hidden rounded-2xl transition-transform duration-200 group-hover:scale-105">
              <FacebookIcon size={56} borderRadius={16} />
            </div>
          </FacebookShareButton>
          <span className="text-xs font-semibold text-aera-slate">
            Facebook
          </span>
        </div>

        <div className="group flex flex-col items-center gap-2">
          <FacebookMessengerShareButton url={shareUrl} appId="">
            <div className="h-14 w-14 overflow-hidden rounded-2xl transition-transform duration-200 group-hover:scale-105">
              <FacebookMessengerIcon size={56} borderRadius={16} />
            </div>
          </FacebookMessengerShareButton>
          <span className="text-xs font-semibold text-aera-slate">
            Messenger
          </span>
        </div>

        <div className="group flex flex-col items-center gap-2">
          <WhatsappShareButton url={shareUrl} title={title} separator=":: ">
            <div className="h-14 w-14 overflow-hidden rounded-2xl transition-transform duration-200 group-hover:scale-105">
              <WhatsappIcon size={56} borderRadius={16} />
            </div>
          </WhatsappShareButton>
          <span className="text-xs font-semibold text-aera-slate">
            WhatsApp
          </span>
        </div>

        <div className="group flex flex-col items-center gap-2">
          <TwitterShareButton
            url={shareUrl}
            title={title}
            hashtags={["Aera", "AI"]}
          >
            <div className="h-14 w-14 overflow-hidden rounded-2xl transition-transform duration-200 group-hover:scale-105">
              <XIcon size={56} borderRadius={16} />
            </div>
          </TwitterShareButton>
          <span className="text-xs font-semibold text-aera-slate">X</span>
        </div>

        <div className="group flex flex-col items-center gap-2">
          <EmailShareButton
            url={shareUrl}
            subject={title}
            body={`Check out this AI worker on Aera: ${shareUrl}`}
          >
            <div className="h-14 w-14 overflow-hidden rounded-2xl transition-transform duration-200 group-hover:scale-105">
              <EmailIcon size={56} borderRadius={16} />
            </div>
          </EmailShareButton>
          <span className="text-xs font-semibold text-aera-slate">Email</span>
        </div>
      </div>

      <div className="border-t border-aera-lightgray/80 pt-6">
        <p className="mb-3 text-sm font-medium text-aera-slate">
          หรือคัดลอกลิงก์
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={shareUrl}
            className="flex-1 rounded-xl border border-aera-lightgray/80 bg-aera-white px-4 py-2.5 text-sm text-aera-charcoal focus:outline-none"
          />
          <AeraButton
            variant="secondary"
            size="md"
            fontWeight="medium"
            onClick={handleCopyLink}
            icon={copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          >
            {copied ? "คัดลอกแล้ว" : "คัดลอก"}
          </AeraButton>
        </div>
      </div>
    </>
  );

  if (isEmbeddedInModal) {
    if (!isOpen) return null;
    return shareBody;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onExited={onExited}
      title="แชร์ข้อมูล"
      size="sm"
      overlayClassName="bg-aera-black/40 backdrop-blur-sm"
      panelClassName="h-auto! max-h-auto! rounded-xl!"
    >
      {shareBody}
    </Modal>
  );
}
